import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { Avatar } from '../../components/ui/Avatar';
import type { Post } from '../posts/api';
import { since } from './api';
import { Verified } from '../../components/ui/Verified';

/**
 * Public updates from across the platform, down the right of the dashboard.
 *
 * ## Public only, and never followers-only
 *
 * Even for a follower who is entitled to see one. This rail mixes strangers in
 * with people you follow, and the moment followers-only writing appears on it,
 * "only my followers can see this" starts depending on which page somebody is
 * looking at. The server enforces it - see `RecentPostsAction` - and this does
 * not filter, because a client that filters is not the rule.
 *
 * ## Absent rather than empty
 *
 * A new platform has no updates, and a column of nothing under a heading reads
 * as something broken. The whole rail disappears until there is something in
 * it, and the grid it lives in closes up behind it.
 *
 * ## Truncated, and linked to the whole thing
 *
 * Four lines and an ellipsis. This is a glance at what people are saying, not a
 * reader - the profile is where an update is read in full, and clicking through
 * is also how somebody discovers the artist, which is the point of the rail.
 */
export function UpdatesRail() {
  const { data, isError } = useQuery({
    queryKey: ['posts', 'recent'],
    queryFn: () => api.get<{ items: Post[] }>('/posts/recent'),
    /*
     * A 404 is the feature being switched off, which is not worth retrying and
     * not worth an error state - it simply means this column does not exist.
     */
    retry: false,
  });

  const items = data?.items ?? [];

  if (isError || items.length === 0) {
    return null;
  }

  return (
    <aside className="flex flex-col gap-3" data-updates-rail>
      <h2 className="meta-caps border-b border-line pb-2 text-faint">
        {copy['noun.post.plural']}
      </h2>

      <ul className="flex flex-col gap-3">
        {items.map((post) => (
          <li key={post.id}>
            <Link
              to={`/${post.author.username}`}
              className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-line p-3 transition-colors hover:border-line-strong hover:bg-raised"
              data-recent-update={post.id}
            >
              <span className="flex items-center gap-2">
                <Avatar
                  src={post.author.avatar}
                  name={post.author.display_name ?? post.author.username ?? ''}
                  size={20}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">
                  {post.author.display_name || post.author.username}
                  {post.author?.verified && <Verified />}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-faint">
                  {since(post.created_at)}
                </span>
              </span>

              {/*
                Clamped rather than sliced in JavaScript, so the cut lands on a
                line rather than mid-word, and a short update keeps its shape.
              */}
              <span className="line-clamp-4 whitespace-pre-wrap text-[13px] leading-relaxed text-soft">
                {post.body}
              </span>

              {post.images.length > 0 && (
                <img
                  src={post.images[0]!.url}
                  alt={post.images[0]!.alt ?? ''}
                  loading="lazy"
                  className="mt-1 h-28 w-full rounded-[var(--radius-control)] border border-line object-cover"
                />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}
